// this service send a request to the backend for very simple analytics with utm parameters
// this mainly serves as a back-up to Google Analytics
// the request to the backend can be made like so:
// curl -X POST "https://energieadvies.bureauverduurzamen.nl/api/analytics/from-url" -H "Content-Type: application/json" -d '{"url":"https://example.com/landing?utm_source=google&utm_medium=cpc&utm_campaign=spring-sale&utm_content=hero-button&utm_term=insulation&utm_id=ad-123","referrer":"https://www.google.com/","method":"POST","persistent_client_id":"550e8400-e29b-41d4-a716-446655440000"}'

import { api } from '@/services/LeadDataService';

const INITIAL_SUBMISSION_STORAGE_KEY = 'basic_analytics_initial_submission';
const PERSISTENT_CLIENT_ID_STORAGE_KEY = 'persistent_client_id';

const generateUuidV4 = () => {
	if (typeof crypto !== 'undefined') {
		if (typeof crypto.randomUUID === 'function') {
			return crypto.randomUUID();
		}

		if (typeof crypto.getRandomValues === 'function') {
			const bytes = crypto.getRandomValues(new Uint8Array(16));
			bytes[6] = (bytes[6] & 0x0f) | 0x40;
			bytes[8] = (bytes[8] & 0x3f) | 0x80;

			const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0'));
			return `${hex.slice(0, 4).join('')}-${hex.slice(4, 6).join('')}-${hex.slice(6, 8).join('')}-${hex.slice(8, 10).join('')}-${hex.slice(10, 16).join('')}`;
		}
	}

	return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (character) => {
		const random = Math.floor(Math.random() * 16);
		const value = character === 'x' ? random : (random & 0x3) | 0x8;
		return value.toString(16);
	});
};

const getCurrentUrl = () => {
	if (typeof window === 'undefined' || !window.location) {
		return null;
	}

	return window.location.href;
};

const getSearchParams = () => {
	if (typeof window === 'undefined' || !window.location) {
		return new URLSearchParams();
	}

	return new URLSearchParams(window.location.search);
};

const hasUtmParamInSearch = () => {
	for (const key of getSearchParams().keys()) {
		if (key.toLowerCase().startsWith('utm_') || key.toLowerCase() === 'gclid' || key.toLowerCase() === 'fbclid') {
			return true;
		}
	}

	return false;
};

const hasStoredInitialSubmission = () => {
	if (typeof window === 'undefined' || !window.sessionStorage) {
		return false;
	}
	return window.sessionStorage.getItem(INITIAL_SUBMISSION_STORAGE_KEY) === '1';
};

const storeInitialSubmission = () => {
	if (typeof window === 'undefined' || !window.sessionStorage) {
		return;
	}
	window.sessionStorage.setItem(INITIAL_SUBMISSION_STORAGE_KEY, '1');
};

const getPersistentClientId = () => {
	if (typeof window === 'undefined' || !window.localStorage) {
		return null;
	}

	return window.localStorage.getItem(PERSISTENT_CLIENT_ID_STORAGE_KEY);
};

const storePersistentClientId = (clientId) => {
	if (typeof window === 'undefined' || !window.localStorage) {
		return null;
	}

	window.localStorage.setItem(PERSISTENT_CLIENT_ID_STORAGE_KEY, clientId);
	return clientId;
};

const getAnalyticsIdentifiers = () => {
	if (typeof window === 'undefined' || !window.location) {
		return {
			uuid: null,
			sessionUuid: null,
		};
	}

	const pathSegments = window.location.pathname.split('/').filter(Boolean);
	const urlIdentifier = pathSegments.length > 0 ? pathSegments[0] : null;

	if (!urlIdentifier) {
		return {
			uuid: null,
			sessionUuid: null,
		};
	}

	if (urlIdentifier.startsWith('sess-')) {
		return {
			uuid: null,
			sessionUuid: urlIdentifier,
		};
	}

	return {
		uuid: urlIdentifier,
		sessionUuid: null,
	};
};

export const BasicAnalyticsService = {
	ensurePersistentClientId() {
		const existingClientId = getPersistentClientId();

		if (existingClientId) {
			return existingClientId;
		}

		return storePersistentClientId(generateUuidV4());
	},

	analyticsEnabled() {
		return hasUtmParamInSearch();
	},

	submitFromCurrentUrl(method = 'GET', view_type = 'unclassified') {
		const url = getCurrentUrl();
		const persistentClientId = this.ensurePersistentClientId();
		const { uuid, sessionUuid } = getAnalyticsIdentifiers();

		if (!url) {
			return Promise.resolve(false);
		}

		return api.post('/analytics/from-url', {
			url,
			referrer: typeof document === 'undefined' ? '' : document.referrer,
			view_type: view_type,
			method,
			persistent_client_id: persistentClientId ? persistentClientId : null,
			uuid: uuid ? uuid : null,
			session_uuid: sessionUuid ? sessionUuid : null,
		});
	},

	submitOnFirstLoadIfUtmPresent() {
		if (!this.analyticsEnabled()) {
			return Promise.resolve(false);
		}

		return this.submitFromCurrentUrl('GET', hasStoredInitialSubmission() ? 'returning' : 'new')
			.then(() => {
				storeInitialSubmission();
				return true;
			})
			.catch((error) => {
				console.error(error);
				return false;
			});
	},
};

